import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, Legend
} from "recharts";
import AppTooltip from "components/Tooltip";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload?.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <p className="font-heading font-semibold text-sm text-foreground mb-1">{label}</p>
        {payload?.map((p) => (
          <p key={p?.dataKey} className="font-data text-xs" style={{ color: p?.color }}>
            {p?.name}: {p?.value?.toFixed(1)}%
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ payload }) => {
  const legendTips = {
    'Estimado': {
      title: 'Margen Estimado',
      description: 'Margen calculado por el motor de pricing en el momento de la compra, basado en benchmark, yield y costes del momento.',
    },
    'Realizado': {
      title: 'Margen Realizado',
      description: 'Margen real obtenido tras la venta, calculado con el precio de venta efectivo y el yield real del lote procesado.',
    },
  };
  return (
    <div className="flex items-center justify-center gap-4 mt-1">
      {payload?.map((entry) => {
        const tip = legendTips?.[entry?.value];
        return (
          <div key={entry?.value} className="flex items-center gap-1.5">
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 2,
                background: entry?.color,
                flexShrink: 0,
              }}
            />
            {tip ? (
              <AppTooltip title={tip?.title} description={tip?.description}>
                <span className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{entry?.value}</span>
              </AppTooltip>
            ) : (
              <span className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{entry?.value}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default function MarginChart({ data }) {
  // Check if all entries are the "no data" fallback (no real margin data)
  const hasRealData = data?.some(d => d?.margenEstimado !== 0 || d?.margenRealizado !== 0);

  return (
    <div className="card p-4 lg:p-5 min-w-0">
      <div className="flex items-center justify-between mb-4">
        <AppTooltip
          title="Márgenes por Categoría"
          description="Compara el margen estimado al comprar cada lote con el margen realmente obtenido al vender. La diferencia revela si estás calibrando bien tus precios de compra."
        >
          <h3 className="font-heading font-semibold text-base text-foreground">Márgenes por Categoría</h3>
        </AppTooltip>
        <span className="text-xs font-caption text-muted-foreground">% margen bruto</span>
      </div>
      {!hasRealData ? (
        <div className="w-full h-48 md:h-56 lg:h-64 flex flex-col items-center justify-center gap-3">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18M9 21V9" />
          </svg>
          <p className="text-sm font-caption text-center" style={{ color: 'var(--color-muted-foreground)' }}>
            Los márgenes se calculan al registrar y cerrar lotes
          </p>
        </div>
      ) : (
        <div className="w-full h-48 md:h-56 lg:h-64" aria-label="Gráfico de márgenes por categoría de metal">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="category"
                tick={{ fontSize: 11, fontFamily: 'IBM Plex Sans', fill: 'var(--color-muted-foreground)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fontFamily: 'JetBrains Mono', fill: 'var(--color-muted-foreground)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}%`}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
              <Bar dataKey="margenEstimado" name="Estimado" radius={[4, 4, 0, 0]}>
                {data?.map((entry, index) => (
                  <Cell key={index} fill={entry?.margenEstimado >= 10 ? 'var(--color-success)' : entry?.margenEstimado >= 5 ? 'var(--color-warning)' : 'var(--color-error)'} />
                ))}
              </Bar>
              <Bar dataKey="margenRealizado" name="Realizado" fill="var(--color-accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}